import { motion } from "framer-motion";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSeo } from "../hooks/useSeo";
import { buildCanonicalUrl } from "../seo/config";
import type { LayoutOutletContext } from "../layout/types";

type RelatedLink = {
  label: string;
  href: string;
  image: string;
};

export const RelatedWebsites = () => {
  const { t } = useTranslation();
  const { locale } = useOutletContext<LayoutOutletContext>();
  const links = t("pages.relatedWebsites.links", {
    returnObjects: true,
  }) as RelatedLink[];
  const siteTitle = t("site.title");
  const pageTitle = t("pages.relatedWebsites.title");

  useSeo({
    title: `${pageTitle} | ${siteTitle}`,
    description: t("pages.relatedWebsites.intro"),
    canonical: buildCanonicalUrl("related-websites/", locale),
    locale,
  });

  const resolveImageSrc = (rawPath: string) => {
    if (!rawPath) return "";
    if (/^https?:\/\//i.test(rawPath)) {
      return rawPath;
    }
    const cleaned = rawPath.replace(/^\/?public\//i, "");
    return `${import.meta.env.BASE_URL}${cleaned.replace(/^\/+/, "")}`;
  };

  return (
    <article className="page page--related related-websites">
      <div className="related-websites__wrapper">
        <div className="related-websites__grid">
          {links.map(({ label, href, image }) => (
            <motion.a
              key={label}
              className="related-websites__link"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              whileHover={{ scale: [null, 1.2, 1.1] }}
              whileTap={{ scale: 1.05 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {image ? (
                <img
                  className="related-websites__link-image"
                  src={resolveImageSrc(image)}
                  alt={label}
                />
              ) : null}
            </motion.a>
          ))}
        </div>
      </div>
    </article>
  );
};
